import type {
  Alignment,
  EmailBlock,
  EmailHeader,
  GalleryColumns,
  GalleryItem,
  GalleryLayout,
  GalleryVerticalAlign,
  IconSet,
  IconStyle,
  ImageMaskShape,
  SocialLink,
  SocialNetwork,
  TiptapContent
} from '../types'
import { normalizeLegacyLinks } from './emailLink'

export interface ApiBlock {
  id: string
  type: 'header' | 'text' | 'image' | 'button' | 'divider' | 'spacer' | 'gallery' | 'footer' | 'social'
  content: Record<string, unknown>
}

export interface ApiStructure {
  subject: string
  previewText?: string
  blocks: ApiBlock[]
  styles?: {
    backgroundColor?: string
    contentBackgroundColor?: string
    fontFamily?: string
  }
}

export interface ApiTemplatePayload {
  name: string
  structure: ApiStructure
}

export interface ApiTemplateResponse {
  id: string
  name: string
  structure: ApiStructure
  created_at: string
  updated_at: string
}

export interface EmailStyles {
  backgroundColor: string
  contentBackgroundColor: string
  fontFamily: string
}

export const defaultEmailStyles: EmailStyles = {
  backgroundColor: 'f4f4f5',
  contentBackgroundColor: 'ffffff',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
}

/**
 * Walk a TipTap doc and return the first text-node fontSize found via the
 * textStyle mark. Used to derive a representative footer font-size for the
 * appended unsubscribe block, so it visually matches the body text.
 */
export const deriveFooterFontSize = (content: TiptapContent | undefined): string | undefined => {
  if (!content || typeof content !== 'object') return undefined
  const walk = (node: TiptapContent): string | null => {
    if (node.type === 'text' && Array.isArray(node.marks)) {
      const ts = node.marks.find((m) => m.type === 'textStyle')
      const fs = ts?.attrs?.fontSize
      if (typeof fs === 'string' && fs) return fs
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        const found = walk(child)
        if (found) return found
      }
    }
    return null
  }
  return walk(content) ?? undefined
}

/**
 * Strip orphan hardBreak nodes inside otherwise-empty paragraphs.
 * The hardBreak is injected by `cleanTiptapContent` at save time so Gmail renders blank lines;
 * we strip it back at load time, otherwise ProseMirror's trailingBreak doubles it visually.
 */
const stripOrphanHardBreaks = (content: TiptapContent): TiptapContent => {
  if (!content || typeof content !== 'object') return content
  const cleaned = { ...content }
  if (Array.isArray(cleaned.content)) {
    cleaned.content = cleaned.content.map(stripOrphanHardBreaks)
  }
  if (
    cleaned.type === 'paragraph' &&
    Array.isArray(cleaned.content) &&
    cleaned.content.length === 1 &&
    cleaned.content[0]?.type === 'hardBreak'
  ) {
    cleaned.content = []
  }
  return cleaned
}

/**
 * Clean TipTap content by removing null/undefined values from marks attrs
 * This ensures the backend receives clean data.
 *
 * Empty top-level paragraphs get a `hardBreak` injected so Gmail/Hotmail render
 * the blank line (an empty `<p>` collapses there). The injection is intentionally
 * scoped to direct children of the `doc` node — paragraphs nested inside list
 * items or blockquotes keep their original empty shape to avoid altering layout
 * semantics in those contexts.
 */
const cleanTiptapContent = (content: TiptapContent, isTopLevel = true): TiptapContent => {
  if (!content || typeof content !== 'object') return content

  const cleaned = { ...content }

  if (cleaned.attrs) {
    cleaned.attrs = Object.fromEntries(
      Object.entries(cleaned.attrs).filter(([, value]) => value !== null && value !== undefined)
    )
    if (Object.keys(cleaned.attrs).length === 0) {
      delete cleaned.attrs
    }
  }

  if (Array.isArray(cleaned.marks)) {
    cleaned.marks = cleaned.marks.map((mark) => {
      if (mark.attrs) {
        const cleanedAttrs = Object.fromEntries(
          Object.entries(mark.attrs).filter(([, value]) => value !== null && value !== undefined)
        )
        return Object.keys(cleanedAttrs).length > 0 ? { ...mark, attrs: cleanedAttrs } : { type: mark.type }
      }
      return { ...mark }
    })
  }

  if (Array.isArray(cleaned.content)) {
    const childIsTopLevel = cleaned.type === 'doc'
    cleaned.content = cleaned.content.map((node) => cleanTiptapContent(node, childIsTopLevel))
  }

  if (isTopLevel && cleaned.type === 'paragraph' && (!Array.isArray(cleaned.content) || cleaned.content.length === 0)) {
    cleaned.content = [{ type: 'hardBreak' }]
  }

  return cleaned
}

/**
 * Transform front-end data to API payload
 */
export function toApiPayload(
  name: string,
  subject: string,
  previewText: string,
  header: EmailHeader,
  blocks: EmailBlock[],
  styles: EmailStyles = defaultEmailStyles
): ApiTemplatePayload {
  const apiBlocks: ApiBlock[] = []

  if (header.logo) {
    apiBlocks.push({
      id: 'header',
      type: 'header',
      content: {
        logoUrl: header.logo,
        align: header.align,
        showLogo: header.showLogo,
        logoWidth: header.logoWidth || undefined,
        link: header.link || undefined,
        blockBackgroundColor: header.blockBackgroundColor ? `#${header.blockBackgroundColor}` : undefined
      }
    })
  }

  for (const block of blocks) {
    const apiBlock = transformBlockToApi(block)

    if (apiBlock) {
      apiBlocks.push(apiBlock)
    }
  }

  return {
    name,
    structure: {
      subject,
      previewText: previewText || undefined,
      blocks: apiBlocks,
      styles: {
        backgroundColor: `#${styles.backgroundColor}`,
        contentBackgroundColor: `#${styles.contentBackgroundColor}`,
        fontFamily: styles.fontFamily
      }
    }
  }
}

const transformBlockToApi = (block: EmailBlock): ApiBlock | null => {
  switch (block.type) {
    case 'text':
      return {
        id: block.id,
        type: 'text',
        content: {
          tiptap: cleanTiptapContent(block.content),
          color: `#${block.color}`,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined,
          fontSize: '16px',
          lineHeight: '24px',
          padding: '10px 40px'
        }
      }

    case 'image':
      return {
        id: block.id,
        type: 'image',
        content: {
          url: block.src,
          ...(block.originalSrc ? { originalUrl: block.originalSrc } : {}),
          alt: block.alt,
          align: block.align,
          maxWidth: `${block.width}%`,
          link: block.link,
          mask: block.mask ?? 'none',
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined
        }
      }

    case 'button':
      return {
        id: block.id,
        type: 'button',
        content: {
          text: block.text,
          url: block.url,
          backgroundColor: `#${block.backgroundColor}`,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined,
          textColor: `#${block.textColor}`,
          align: block.align,
          borderRadius: '8px'
        }
      }

    case 'spacer':
      return {
        id: block.id,
        type: 'spacer',
        content: {
          height: `${block.height}px`,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined
        }
      }

    case 'separator':
      return {
        id: block.id,
        type: 'divider',
        content: {
          color: `#${block.color}`,
          thickness: '1px',
          height: `${block.height}px`,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined
        }
      }

    case 'gallery':
      return {
        id: block.id,
        type: 'gallery',
        content: {
          layout: block.layout,
          columns: block.columns,
          gap: block.gap,
          padding: '20px 40px',
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined,
          items: block.items.map((item) =>
            item.type === 'image'
              ? {
                  type: 'image',
                  url: item.src,
                  alt: item.alt,
                  link: item.link,
                  width: item.naturalWidth,
                  height: item.naturalHeight,
                  borderRadius: '8px'
                }
              : {
                  type: 'text',
                  tiptap: cleanTiptapContent(item.content),
                  color: `#${item.color}`,
                  align: item.align,
                  verticalAlign: item.verticalAlign,
                  fontSize: '14px',
                  lineHeight: '22px'
                }
          )
        }
      }

    case 'social':
      return {
        id: block.id,
        type: 'social',
        content: {
          links: block.links.map((l) => ({ network: l.network, url: l.url })),
          align: block.align,
          iconSet: block.iconSet,
          iconSize: block.iconSize,
          iconStyle: block.iconStyle,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined
        }
      }

    case 'footer': {
      const derivedFontSize = deriveFooterFontSize(block.content)
      return {
        id: block.id,
        type: 'footer',
        content: {
          tiptap: cleanTiptapContent(block.content),
          align: block.align,
          color: `#${block.color}`,
          blockBackgroundColor: block.blockBackgroundColor ? `#${block.blockBackgroundColor}` : undefined,
          logoUrl: block.logoSrc,
          companyName: block.companyName || undefined,
          tagline: block.tagline || undefined,
          address: block.address || undefined,
          contact: block.contact || undefined,
          ...(derivedFontSize ? { fontSize: derivedFontSize } : {})
        }
      }
    }

    default:
      return null
  }
}

/**
 * Transform API response to front-end data
 */
export function fromApiResponse(response: ApiTemplateResponse): {
  name: string
  subject: string
  previewText: string
  header: EmailHeader
  blocks: EmailBlock[]
  styles: EmailStyles
} {
  const header: EmailHeader = {
    logo: '',
    showLogo: false,
    align: 'center',
    link: ''
  }

  const blocks: EmailBlock[] = []

  const apiBlocks = response?.structure?.blocks || []

  for (const apiBlock of apiBlocks) {
    if (apiBlock.type === 'header') {
      header.logo = (apiBlock.content.logoUrl as string) || ''
      header.showLogo =
        apiBlock.content.showLogo !== undefined ? (apiBlock.content.showLogo as boolean) : !!apiBlock.content.logoUrl
      header.align = (apiBlock.content.align as 'left' | 'center' | 'right') || 'center'
      header.link = (apiBlock.content.link as string) || ''
      header.logoWidth = typeof apiBlock.content.logoWidth === 'number' ? apiBlock.content.logoWidth : undefined
      header.blockBackgroundColor = backgroundColorFromApi(apiBlock.content.blockBackgroundColor)
    } else {
      const block = transformBlockFromApi(apiBlock)
      if (block) {
        blocks.push(block)
      }
    }
  }

  const apiStyles = response?.structure?.styles
  const styles: EmailStyles = {
    backgroundColor: apiStyles?.backgroundColor
      ? removeHashFromColor(apiStyles.backgroundColor)
      : defaultEmailStyles.backgroundColor,
    contentBackgroundColor: apiStyles?.contentBackgroundColor
      ? removeHashFromColor(apiStyles.contentBackgroundColor)
      : defaultEmailStyles.contentBackgroundColor,
    fontFamily: apiStyles?.fontFamily || defaultEmailStyles.fontFamily
  }

  return {
    name: response?.name || '',
    subject: response?.structure?.subject || '',
    previewText: response?.structure?.previewText || '',
    header,
    blocks,
    styles
  }
}

function transformBlockFromApi(apiBlock: ApiBlock): EmailBlock | null {
  const content = apiBlock.content

  switch (apiBlock.type) {
    case 'text':
      if ((content.isTitle || content.heading) && !content.tiptap) {
        const headingText = (content.heading as string) || ''
        return {
          id: apiBlock.id,
          type: 'text',
          content: createHeadingTiptapContent(headingText),
          align: (content.align as 'left' | 'center' | 'right' | 'justify') || 'left',
          color: removeHashFromColor(content.color as string),
          blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
        }
      }
      return {
        id: apiBlock.id,
        type: 'text',
        content: content.tiptap
          ? normalizeLegacyLinks(stripOrphanHardBreaks(content.tiptap as TiptapContent))
          : createDefaultTiptapContent((content.text as string) || ''),
        align: (content.align as 'left' | 'center' | 'right' | 'justify') || 'left',
        color: removeHashFromColor(content.color as string),
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }

    case 'image': {
      const rawMask = content.mask as string | undefined
      const validMasks: ImageMaskShape[] = ['none', 'rounded', 'circle']
      const mask: ImageMaskShape = validMasks.includes(rawMask as ImageMaskShape) ? (rawMask as ImageMaskShape) : 'none'
      const src = (content.url as string) || ''
      const originalSrc = (content.originalUrl as string) || src
      return {
        id: apiBlock.id,
        type: 'image',
        src,
        originalSrc,
        alt: (content.alt as string) || '',
        width: parseWidthFromString(content.maxWidth as string),
        align: (content.align as Alignment) || 'center',
        link: (content.link as string) || '',
        mask,
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }
    }

    case 'button':
      return {
        id: apiBlock.id,
        type: 'button',
        text: (content.text as string) || '',
        url: (content.url as string) || '',
        backgroundColor: removeHashFromColor(content.backgroundColor as string),
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor),
        textColor: removeHashFromColor(content.textColor as string),
        align: (content.align as 'left' | 'center' | 'right' | 'justify') || 'center'
      }

    case 'spacer':
      return {
        id: apiBlock.id,
        type: 'spacer',
        height: parseHeightFromString(content.height as string),
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }

    case 'divider':
      return {
        id: apiBlock.id,
        type: 'separator',
        color: removeHashFromColor(content.color as string),
        height: parseHeightFromString((content.height as string) || '16px'),
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }

    case 'gallery': {
      const rawColumns = Number(content.columns)
      const columns: GalleryColumns = rawColumns === 3 ? 3 : rawColumns === 1 ? 1 : 2
      const rawGap = Number(content.gap)
      const gap = Number.isFinite(rawGap) ? rawGap : 16
      const rawLayout = content.layout
      const layout: GalleryLayout = rawLayout === 'asymmetric-right' ? 'asymmetric-right' : 'grid'
      const rawItems = Array.isArray(content.items) ? (content.items as Array<Record<string, unknown>>) : []
      const items: GalleryItem[] = rawItems.map((item) => {
        if (item.type === 'text') {
          const rawVAlign = item.verticalAlign
          const verticalAlign: GalleryVerticalAlign =
            rawVAlign === 'middle' || rawVAlign === 'bottom' ? rawVAlign : 'top'
          return {
            type: 'text',
            content: item.tiptap
              ? normalizeLegacyLinks(stripOrphanHardBreaks(item.tiptap as TiptapContent))
              : createDefaultTiptapContent((item.text as string) || ''),
            color: removeHashFromColor(item.color as string),
            align: (item.align as Alignment) || 'left',
            verticalAlign
          }
        }
        const width = Number(item.width)
        const height = Number(item.height)
        const hasDimensions = width > 0 && height > 0
        return {
          type: 'image',
          src: (item.url as string) || '',
          alt: (item.alt as string) || '',
          link: (item.link as string) || '',
          ...(hasDimensions ? { naturalWidth: width, naturalHeight: height } : {})
        }
      })
      return {
        id: apiBlock.id,
        type: 'gallery',
        layout,
        columns,
        gap,
        items,
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }
    }

    case 'footer': {
      const merchantFields = {
        companyName: (content.companyName as string) || undefined,
        tagline: (content.tagline as string) || undefined,
        address: (content.address as string) || undefined,
        contact: (content.contact as string) || undefined
      }
      if (content.tiptap) {
        return {
          id: apiBlock.id,
          type: 'footer',
          logoSrc: (content.logoUrl as string) || '',
          content: normalizeLegacyLinks(stripOrphanHardBreaks(content.tiptap as TiptapContent)),
          align: (content.align as 'left' | 'center' | 'right') || 'center',
          color: removeHashFromColor(content.color as string),
          blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor),
          ...merchantFields
        }
      }
      return {
        id: apiBlock.id,
        type: 'footer',
        logoSrc: (content.logoUrl as string) || '',
        content: createDefaultTiptapContent(''),
        align: (content.align as 'left' | 'center' | 'right') || 'center',
        color: removeHashFromColor(content.color as string),
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor),
        ...merchantFields
      }
    }

    case 'social': {
      const rawLinks = Array.isArray(content.links) ? (content.links as Array<Record<string, unknown>>) : []
      const validNetworks: SocialNetwork[] = [
        'facebook',
        'instagram',
        'tiktok',
        'twitter',
        'linkedin',
        'youtube',
        'pinterest',
        'whatsapp',
        'website'
      ]
      const links: SocialLink[] = rawLinks
        .filter((l) => validNetworks.includes(l.network as SocialNetwork))
        .map((l, i) => ({
          id: `${apiBlock.id}-link-${i}`,
          network: l.network as SocialNetwork,
          url: (l.url as string) || ''
        }))
      const validSets: IconSet[] = ['brand', 'dark', 'light']
      return {
        id: apiBlock.id,
        type: 'social',
        links,
        align: (content.align as Alignment) || 'center',
        iconSet: validSets.includes(content.iconSet as IconSet) ? (content.iconSet as IconSet) : 'brand',
        iconSize: typeof content.iconSize === 'number' ? content.iconSize : 40,
        iconStyle: (content.iconStyle as IconStyle | undefined) ?? 'filled',
        blockBackgroundColor: backgroundColorFromApi(content.blockBackgroundColor)
      }
    }

    default:
      return null
  }
}

const removeHashFromColor = (color: string | undefined): string => {
  if (!color) return '000000'
  return color.replace(/^#/, '')
}

/**
 * Per-block background is optional/transparent by default, so keep it `undefined`
 * when the API omits it (instead of defaulting to a color like `removeHashFromColor`).
 */
const backgroundColorFromApi = (color: unknown): string | undefined =>
  typeof color === 'string' && color ? color.replace(/^#/, '') : undefined

const createDefaultTiptapContent = (text: string): TiptapContent => {
  if (!text) {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }]
    }
  }

  const lines = text.replace(/<br\s*\/?>/gi, '\n').split('\n')
  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : []
    }))
  }
}

const createHeadingTiptapContent = (text: string): TiptapContent => {
  return {
    type: 'doc',
    content: [
      {
        type: 'heading',
        attrs: { level: 1 },
        content: text ? [{ type: 'text', text }] : []
      }
    ]
  }
}

const parseHeightFromString = (height: string | undefined): number => {
  if (!height) return 16
  const match = height.match(/(\d+)/)
  return match ? parseInt(match[1], 10) : 16
}

const parseWidthFromString = (width: string | undefined): number => {
  if (!width) return 100
  const match = width.match(/(\d+)/)
  if (!match) return 100
  const n = parseInt(match[1], 10)
  return n >= 1 && n <= 100 ? n : 100
}
